import type { EventBus } from "../../core/events/EventBus";
import type { GameEventName } from "../../core/events/GameEvents";
import type { KnowledgeGraph } from "../../knowledge/KnowledgeGraph";
import type { World } from "../World";
import type { ActionRegistry } from "./ActionRegistry";
import type { ConditionRegistry } from "./ConditionRegistry";
import type { RuleAction, RuleCondition } from "./types";

export interface SecretDefinitionLike {
  readonly id: string;
  readonly condition: RuleCondition;
  readonly actions: readonly RuleAction[];
}

/**
 * Evaluates secrets (and, via {@link QuestEngine}, quest steps) against game events.
 * A secret/step "when" is only checked on the one event its condition type declares,
 * so this never polls — it reacts.
 */
export class RuleEngine {
  private readonly wiredEvents = new Set<GameEventName>();

  constructor(
    private readonly events: EventBus,
    private readonly conditions: ConditionRegistry,
    private readonly actions: ActionRegistry,
    private readonly world: World,
    private readonly knowledge: KnowledgeGraph,
  ) {}

  /** Wires secret evaluation for every event type referenced by the scene's own secrets. */
  wireSceneSecrets(secrets: readonly SecretDefinitionLike[]): void {
    for (const secret of secrets) {
      const event = this.conditions.eventFor(secret.condition.type);
      if (!event) throw new Error(`Unknown secret condition type: ${secret.condition.type}`);
      this.ensureWired(event);
    }
    this.pendingSecrets = secrets;
  }

  private pendingSecrets: readonly SecretDefinitionLike[] = [];

  private ensureWired(event: GameEventName): void {
    if (this.wiredEvents.has(event)) return;
    this.wiredEvents.add(event);
    this.events.on(event, (payload) => this.onEvent(event, payload));
  }

  private onEvent<K extends GameEventName>(event: K, payload: unknown): void {
    for (const secret of this.pendingSecrets) {
      if (this.world.isSecretRevealed(secret.id)) continue;
      if (this.conditions.eventFor(secret.condition.type) !== event) continue;
      if (!this.conditions.matches(secret.condition.type, payload as never, secret.condition)) continue;
      for (const action of secret.actions) {
        this.actions.execute({ world: this.world, knowledge: this.knowledge, events: this.events }, action);
      }
      this.world.markSecretRevealed(secret.id);
      this.events.emit("secret:revealed", { secretId: secret.id });
    }
  }
}
